import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { noop } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
/* Models */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { DealsModel, PATHS as DEALS_PATHS } from 'client/data/models/deals';
/* Utils */
import { getSavings } from 'site-modules/core-page/utils/crowdsourced-deals-utils';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { EventToolbox } from 'client/utils/event-toolbox';
/* Constants */
import {
  CROWDSOURCED_DEALS_EVENTS,
  CORA_LOGO,
  BEST_DEAL_CTA,
} from 'site-modules/core-page/constants/crowdsourced-deals';
/* Components */
import { Link } from 'site-modules/shared/components/link/link';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

import './crowdsourced-deals-savings.scss';

export function CrowdsourcedDealsSavingsUI({
  chal = PropTypes.string,
  vehicle = null,
  location = {},
  defaultMsrp = 0,
  csdPercentOff = null,
  setIsModalOpened = noop,
}) {
  const [savings, setSavings] = useState(() =>
    csdPercentOff && defaultMsrp ? getSavings(csdPercentOff, defaultMsrp) : {}
  );

  const isChal1 = chal === 'chal-1';
  const isChal2 = chal === 'chal-2';
  const isChal3Chal4 = ['chal-3', 'chal-4'].includes(chal);

  const prevCsdPercentOff = usePrevious(csdPercentOff);
  const prevDefaultMsrp = usePrevious(defaultMsrp);

  if (csdPercentOff && defaultMsrp && (prevCsdPercentOff !== csdPercentOff || prevDefaultMsrp !== defaultMsrp)) {
    setSavings(getSavings(csdPercentOff, defaultMsrp));
  }

  if (!(csdPercentOff && defaultMsrp)) return null;

  const coraLogo = (
    <img
      alt="Cora logo"
      src={getStaticImageUrl(CORA_LOGO)}
      className={classNames('d-inline-block', { 'ms-0_25': isChal2, 'mb-1': isChal3Chal4 })}
      height={isChal2 ? 16 : 20}
      loading="lazy"
    />
  );

  const handleClick = () => {
    EventToolbox.fireCustomEvent(CROWDSOURCED_DEALS_EVENTS.QUOTE_BOT_START, {
      value: BEST_DEAL_CTA,
    });
    if (isChal1) {
      setIsModalOpened(true);
    }
  };

  return (
    <div className="crowdsourced-deals-savings-container rounded-12 bg-cool-gray-90 p-1">
      {isChal3Chal4 && coraLogo}
      <h3 className="size-16 text-cool-gray-30 mb-1">
        Who you buy {vehicle?.model?.name} from near {location?.city} makes a big difference
      </h3>
      <div className="mb-0_5">Best offer: {formatPriceStringV2(savings?.min)} below MSRP</div>
      <div>Worst offer: {formatPriceStringV2(savings?.max)} above MSRP</div>
      {chal === 'chal-1' ? (
        <Button
          className="py-0_75 px-1_5 btn-blue-50 text-transform-none size-16 fw-medium w-100 mt-1 mb-0_5"
          onClick={handleClick}
        >
          {BEST_DEAL_CTA}
        </Button>
      ) : (
        <>
          <Link
            to="https://savings.coracar.com/"
            target="_blank"
            className={classNames(
              'best-deal-btn d-block bg-blue-50 text-white text-decoration-none py-0_75 px-1 fw-medium mt-1',
              chal,
              { 'mb-1': isChal2 }
            )}
            onClick={handleClick}
          >
            {BEST_DEAL_CTA}
            <i className="icon-open_in_new ms-0_25" aria-hidden />
          </Link>
          {isChal2 && (
            <div className="text-center size-12 fw-medium text-gray-30">
              POWERED BY
              {coraLogo}
            </div>
          )}
        </>
      )}
    </div>
  );
}

CrowdsourcedDealsSavingsUI.propTypes = {
  chal: PropTypes.string,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  defaultMsrp: PropTypes.number,
  csdPercentOff: PropTypes.shape({}),
  location: VisitorEntities.Location,
  setIsModalOpened: PropTypes.func,
};

export const propsAreEqual = (prevProps, nextProps) => !(nextProps.csdPercentOff || nextProps.defaultMsrp);
export const stateToPropsConfig = {
  location: bindToPath('location', VisitorModel),
  csdPercentOff: bindToPath(
    ({ vehicle }) =>
      vehicle?.make?.slug && vehicle?.model.slug && vehicle?.year
        ? DEALS_PATHS.CSD_PERCENT_OFF({
            make: vehicle.make.slug,
            model: vehicle.model.slug,
            year: vehicle.year,
          })
        : null,
    DealsModel
  ),
};

export const CrowdsourcedDealsSavings = connectToModel(
  React.memo(CrowdsourcedDealsSavingsUI, propsAreEqual),
  stateToPropsConfig
);
